'use client';

import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import {
  SalePersonalFieldsStepOne,
  SalePersonalFieldsStepTwo,
  useSaleApplicationForm,
} from '@/features/original-sale';
import { SaleBankFields } from './sale-bank-fields';
import { SaleBankInfoBlock } from './sale-bank-info-block';
import CrossErrorIcon from '@/components/icons/cross-error';
import config from '@/config';
import { SALE_OFFER_DOCUMENT_SLUG, SALE_OFFER_PDF_LINK_TEXT } from '../constants/sale.constants';
import type { SellerProfileResponse } from '@/features/original-sale/types/sale.types';

const SALE_FORM_STEP_ONE = 1;
const SALE_FORM_STEP_TWO = 2;

interface SaleApplicationFormProps {
  fanficId?: string;
  onSuccess?: () => void;
  profile?: SellerProfileResponse | null;
}

export const SaleApplicationForm = ({ fanficId, onSuccess, profile }: SaleApplicationFormProps) => {
  const { form, currentStep, onSubmit, isSubmitting, isValid } = useSaleApplicationForm({
    fanficId,
    onSuccess,
    profile,
  });

  return (
    <div className="bg-white dark:bg-card rounded-xl p-6 space-y-8">
      <div className="relative space-y-2">
        <span className="absolute top-0 right-0 text-md font-medium text-[#888C91]">
          {currentStep}/{SALE_FORM_STEP_TWO}
        </span>

        <div className="text-center space-y-2 pr-10">
          <h3 className="text-xl font-bold text-[#1B1D22]">Заявление на подключение</h3>
          <p className="text-sm text-muted-foreground">
            Для продажи ориджиналов на платформе, пожалуйста, заполните заявление на подключение.
            После отправки анкеты с вами свяжется менеджер для персонального сопровождения.
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-8">
          {currentStep === SALE_FORM_STEP_ONE ? (
            <>
              <SalePersonalFieldsStepOne />

              <FormField
                control={form.control}
                name="consent"
                render={({ field, fieldState }) => (
                  <FormItem className="space-y-3">
                    {fieldState.error && (
                      <div className="flex items-center gap-2 text-sm font-normal text-destructive">
                        <CrossErrorIcon />
                        <span>{fieldState.error.message}</span>
                      </div>
                    )}
                    <div className="flex flex-row items-center space-x-2">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <span className="text-sm leading-none">
                        Я соглашаюсь с условиями{' '}
                        <Link
                          href={`${config.API_URL}/api/v1/documents/${SALE_OFFER_DOCUMENT_SLUG}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-[#505CFF] hover:opacity-80"
                        >
                          {SALE_OFFER_PDF_LINK_TEXT}
                        </Link>
                      </span>
                    </div>
                  </FormItem>
                )}
              />
            </>
          ) : (
            <>
              <SalePersonalFieldsStepTwo />
              <SaleBankInfoBlock />
              <SaleBankFields />
            </>
          )}

          <Button
            type="submit"
            variant="purple"
            disabled={isSubmitting || !isValid}
            className="w-full max-w-[165px] h-10 rounded-xl bg-[#505CFF] hover:bg-[#505CFF]/90  text-white font-medium"
          >
            {isSubmitting ? 'Отправка...' : 'Отправить заявление'}
          </Button>
        </form>
      </Form>
    </div>
  );
};
